import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";

function MobileMarketingContentSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      fromDate: "",
      toDate: "",
      status: "",
      duration: "",
      contentType: "",
      content: "",
      url: "",
      base64String: "",
      createdBy: localStorage.getItem("userId"),
    },
  });

  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [marketingContentData, setMarketingContentData] = useState([]);

  const getMobileMarketingData = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(`SMSApp/GetMobilePublicityContent`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMarketingContentData(response?.mobilePublicityContent);
    } catch (error) {
      console.error("Error fetching marketing data:", error);
    }
  };

  useEffect(() => {
    getMobileMarketingData();
  }, [triggerRerender]);

  const handleEdit = async (rowId) => {
    try {
      const response = await get(
        `SMSApp/GetMobilePublicityContentById?id=${rowId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const singleMarketingData = response?.mobilePublicityContent;

      setPreviewUrl(null);

      const fileUrl = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${singleMarketingData.url}`;

      setTimeout(() => {
        if (singleMarketingData.contentType === "Video") {
          setPreviewUrl(fileUrl);
        } else if (singleMarketingData.contentType === "Image") {
          setPreviewUrl(fileUrl);
        } else {
          setPreviewUrl(null);
        }
      }, 50);

      setFileName(singleMarketingData.content);

      reset({
        ...singleMarketingData,
        id: singleMarketingData.id,
        fromDate: singleMarketingData.fromDate.split("T")[0],
        toDate: singleMarketingData.toDate.split("T")[0],
        updatedBy: localStorage.getItem("userId"),
      });
    } catch (error) {
      console.error("Error editing marketing:", error);
    }
  };

  const columns = [
    { name: "ID", selector: (row) => row.id, sortable: true, omit: true },
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "File Name",
      selector: (row) => row.content,
      sortable: true,
      width: "250px",
    },
    {
      name: "Type",
      selector: (row) => row.contentType,
      sortable: true,
      width: "80px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      width: "80px",
    },
    {
      name: "Action",
      width: "60px",
      selector: (row) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn p-0"
            onClick={() => handleEdit(row.id)}
          >
            <i className="fas fa-pencil"></i>
          </button>
        </div>
      ),
    },
  ];

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setFileName(file.name);

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        setPreviewUrl(result); // Set the result (image/video base64)

        const base64String = result.split(",")[1];
        setValue("base64String", base64String, { shouldValidate: true });

        // Set contentType based on file type (image or video)
        const fileType = file.type; // Get MIME type
        const contentType = fileType.startsWith("image/")
          ? "Image"
          : fileType.startsWith("video/")
          ? "Video"
          : "";

        // Set the contentType value
        setValue("contentType", contentType, { shouldValidate: true });

        trigger("content");
      };
      reader.readAsDataURL(file); // Read as base64 data URL for preview
    } else {
      setPreviewUrl(null); // Clear preview if no file is selected
    }
  };

  const calculateStatus = (fromDate, toDate) => {
    const currentDate = new Date();
    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (isNaN(from) || isNaN(to)) {
      return "Inactive"; // Invalid dates
    }

    if (currentDate < from) {
      return "Scheduled"; // Start date is in the future
    } else if (currentDate >= from && currentDate <= to) {
      return "Active"; // Current date is within the range
    } else if (currentDate > to) {
      return "Expired"; // End date is in the past
    }

    return "inactive"; // Default fallback
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const status = calculateStatus(data.fromDate, data.toDate);

    const formData = {
      ...data,
      status,
      content: fileName,
    };

    // console.log(formData);
    // return;

    try {
      const response = await post(
        "SMSApp/AddUpdateMobilePublicityContent",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        setPreviewUrl(null);
        reset({
          id: 0,
          fromDate: "",
          toDate: "",
          status: "",
          duration: "",
          contentType: "",
          content: "",
          url: "",
          base64String: "",
          createdBy: localStorage.getItem("userId"),
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Mobile Marketing Content</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form className="row gx-2 gy-3" onSubmit={handleSubmit(saveData)}>
                <div className="col-md-6">
                  <label className="form-label">
                    From Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`fromDate`, {
                      required: "This field is required.",
                      validate: {
                        lessThantoDate: (value) => {
                          const toDate = getValues(`toDate`);
                          return (
                            !toDate ||
                            new Date(value) < new Date(toDate) ||
                            "Must be less than To Date."
                          );
                        },
                      },
                    })}
                    type="date"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.fromDate && (
                    <small className="text-danger">
                      {errors.fromDate.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    To Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`toDate`, {
                      required: "This field is required.",
                      validate: {
                        greaterThanfromDate: (value) => {
                          const fromDate = getValues(`fromDate`);
                          return (
                            !fromDate ||
                            new Date(value) > new Date(fromDate) ||
                            "Must be greater than From Date."
                          );
                        },
                      },
                    })}
                    type="date"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.toDate && (
                    <small className="text-danger">
                      {errors.toDate.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Picture Or Video
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <div className="image-uploader-wrapper-2 d-flex flex-column align-items-center">
                    <div className="image-preview d-flex justify-content-center align-items-center flex-shrink-0 mb-2">
                      {previewUrl ? (
                        previewUrl.startsWith("data:image/") ||
                        /\.(jpeg|jpg|png|gif|bmp|webp)$/i.test(previewUrl) ? (
                          <img src={previewUrl} alt="Preview" />
                        ) : previewUrl.startsWith("data:video/") ||
                          /\.(mp4|webm|ogg)$/i.test(previewUrl) ? (
                          <video controls autoPlay={false}>
                            <source src={previewUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <div>Unsupported file format</div>
                        )
                      ) : (
                        <div>
                          <span className="fas fa-image fs-3 text-light"></span>
                        </div>
                      )}
                    </div>
                    <input
                      {...register("content", {
                        required: !previewUrl
                          ? "This field is required."
                          : false, // Conditionally set required
                      })}
                      type="file"
                      className="form-control"
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                    />
                  </div>
                  {errors.content && (
                    <small className="text-danger">
                      {errors.content.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Duration (Seconds)
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`duration`, {
                      required: "This field is required.",
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.duration && (
                    <small className="text-danger">
                      {errors.duration.message}
                    </small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <DataTable
                columns={columns}
                data={marketingContentData}
                pagination
                dense
                customStyles={customTableStyles}
                highlightOnHover={true}
                onChangePage={setCurrentPage}
                onChangeRowsPerPage={setRowsPerPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MobileMarketingContentSetup;
